import axios from 'axios'
import {API_URL} from '../../../../_metronic/helpers'
import {
  FilterSummaryItem,
  SectionOption,
  ServiceFee,
  ServiceSms,
  ServiceTariff,
  SmsOutcome,
  SmsSample,
  faDigits,
} from './serviceApi'
import {RecipientCandidate} from './recipients'

// «گزارش‌ها ← گزارش‌های دوره‌ای»: api/business-services/reports.

export type ReportInterval = {hours: number; title: string}

export type ReportLimits = {
  minHours: number
  maxHours: number
  maxSchedules: number
  maxRecipients: number
}

export type ReportLastRun = {
  token: string
  at?: string | null
  atLabel?: string | null
  /** running · completed · failed · unpaid. */
  state?: string | null
  /** What became of the run's SMS, as the server sends it (BusinessReportService.ScheduleView). */
  sms?: SmsOutcome | null
}

/** The title of a last run's SMS outcome («ارسال شد»…), or undefined when there was none. */
export const lastRunSmsTitle = (run?: ReportLastRun | null): string | undefined =>
  run?.sms && run.sms.state !== 'none' ? run.sms.title : undefined

export type ReportSchedule = {
  id: number
  title: string
  enabled: boolean
  intervalHours: number
  intervalTitle: string
  /** HH:mm, Tehran. */
  sendTime: string
  /** Every mobile texted now (see core/recipients). */
  recipients: string[]
  /** The users of the business who get the SMS (backend 1.0.0.639+). */
  recipientUserIds?: number[] | null
  /** Mobiles entered before that belong to no user of the business. */
  otherRecipients?: string[] | null
  smsEnabled: boolean
  quietHours: boolean
  hideZeroLines: boolean
  sections: string[]
  /** The invoice list's `filter_*` query string, without date keys. */
  filter: string
  filterSummary: FilterSummaryItem[]
  nextRunAt?: string | null
  nextRunAtLabel?: string | null
  lastRun?: ReportLastRun | null
}

export type ReportScheduleInput = {
  title: string
  enabled: boolean
  intervalHours: number
  sendTime: string
  /** With recipientUserIds: the mobiles kept from before; without (an older backend): every mobile. */
  recipients: string[]
  recipientUserIds?: number[]
  smsEnabled: boolean
  quietHours: boolean
  hideZeroLines: boolean
  sections: string[]
  filter: string
  confirmCharge?: boolean
}

export type ReportSettingsData = {
  available: boolean
  companyId: number
  canChange: boolean
  schedules: ReportSchedule[]
  sms: ServiceSms
  tariffs?: ServiceTariff[] | null
  sectionCatalog: SectionOption[]
  intervals: Array<ReportInterval | number>
  limits: ReportLimits
  /** The sections a new schedule starts with. */
  defaultSections?: string[] | null
  /** The users the SMS recipients are chosen from (backend 1.0.0.639+). */
  recipientCandidates?: RecipientCandidate[] | null
}

// ---- snapshot --------------------------------------------------------------------------------------------

export type SnapshotSlice = {key: string; label: string; count: number; amount?: number | null}

export type SnapshotPoint = {
  key: string
  label: string
  created: number
  sent: number
  registered: number
  rejected: number
}

export type ReportSnapshot = {
  version: number
  periodFrom: string
  periodTo: string
  periodLabel: string
  intervalHours: number
  generatedAt?: string | null
  generatedAtLabel?: string | null
  filterSummary?: FilterSummaryItem[] | null
  created: {
    count: number
    amount: number
    vat: number
    byChannel?: SnapshotSlice[] | null
    bySubject?: SnapshotSlice[] | null
    byType?: SnapshotSlice[] | null
    byPattern?: SnapshotSlice[] | null
    byStatus?: SnapshotSlice[] | null
    reactions?: SnapshotSlice[] | null
    article6?: SnapshotSlice[] | null
  }
  sent: {
    count: number
    attempts: number
    registered: number
    rejected: number
    pending: number
    unknown: number
    /** Created in the window and not sent yet. */
    notSent?: number | null
    registeredAmount: number
    registeredVat: number
    byStatus?: SnapshotSlice[] | null
    reactions?: SnapshotSlice[] | null
    article6?: SnapshotSlice[] | null
  }
  series?: {bucket: 'hour' | 'day' | string; points: SnapshotPoint[]} | null
  season?: {
    key: string
    title: string
    total: number
    amount: number
    statuses?: SnapshotSlice[] | null
  } | null
  topErrors?: Array<{message: string; count: number}> | null
  topBuyers?: Array<{name: string; count: number; amount: number}> | null
  reactionsDetected?: {
    approved: number
    rejected: number
    systemic: number
    article6: number
  } | null
}

export type ReportPreview = {snapshot: ReportSnapshot; sms: SmsSample}

export type ReportPreviewInput = {
  intervalHours: number
  sections: string[]
  filter: string
  hideZeroLines: boolean
}

export type ReportRunTrigger = 'scheduled' | 'manual' | string

export type ReportRunRow = {
  token: string
  trigger: ReportRunTrigger
  periodFrom?: string | null
  periodTo?: string | null
  periodLabel: string
  createdAt?: string | null
  createdAtLabel?: string | null
  state: 'running' | 'completed' | 'failed' | 'unpaid' | string
  error?: string | null
  sms?: SmsOutcome | null
  fee?: ServiceFee | null
  headline?: {created: number; sent: number; registered: number; rejected: number} | null
}

export type ReportRunsPage = {items: ReportRunRow[]; total: number; page: number; pageSize: number}

export type ReportRun = {
  token: string
  companyId: number
  businessName?: string | null
  schedule?: {id: number; title: string; intervalTitle: string} | null
  trigger: ReportRunTrigger
  createdAt?: string | null
  createdAtLabel?: string | null
  state?: string | null
  fee?: ServiceFee | null
  sms?: SmsOutcome | null
  snapshot: ReportSnapshot
}

const BASE = `${API_URL}/business-services/reports`

export const reportKeys = {
  settings: (companyId: number) => ['periodic-reports', 'settings', companyId],
  runs: (companyId: number, scheduleId: number, page?: number) =>
    page
      ? ['periodic-reports', 'runs', companyId, scheduleId, page]
      : ['periodic-reports', 'runs', companyId, scheduleId],
  run: (token: string) => ['periodic-reports', 'run', token],
  preview: (companyId: number, input: ReportPreviewInput) => [
    'periodic-reports',
    'preview',
    companyId,
    input,
  ],
}

export const getReportSettings = async (companyId: number): Promise<ReportSettingsData> =>
  (await axios.get<ReportSettingsData>(BASE, {params: {companyId}})).data

export const createReportSchedule = async (
  companyId: number,
  input: ReportScheduleInput
): Promise<ReportSchedule> =>
  (await axios.post<ReportSchedule>(BASE, input, {params: {companyId}})).data

export const updateReportSchedule = async (
  companyId: number,
  id: number,
  input: ReportScheduleInput
): Promise<ReportSchedule> =>
  (await axios.put<ReportSchedule>(`${BASE}/${id}`, input, {params: {companyId}})).data

export const deleteReportSchedule = async (
  companyId: number,
  id: number
): Promise<{deleted: boolean}> =>
  (await axios.delete<{deleted: boolean}>(`${BASE}/${id}`, {params: {companyId}})).data

export const previewReport = async (
  companyId: number,
  input: ReportPreviewInput
): Promise<ReportPreview> =>
  (await axios.post<ReportPreview>(`${BASE}/preview`, input, {params: {companyId}})).data

export const runReportNow = async (
  companyId: number,
  id: number,
  sendSms: boolean
): Promise<{token: string}> =>
  (await axios.post<{token: string}>(`${BASE}/${id}/run`, {sendSms}, {params: {companyId}})).data

export const getReportRuns = async (
  companyId: number,
  id: number,
  page: number,
  pageSize: number
): Promise<ReportRunsPage> =>
  (await axios.get<ReportRunsPage>(`${BASE}/${id}/runs`, {params: {companyId, page, pageSize}}))
    .data

export const getReportRun = async (token: string): Promise<ReportRun> =>
  (await axios.get<ReportRun>(`${BASE}/runs/${encodeURIComponent(token)}`)).data

// ---- wording -----------------------------------------------------------------------------------------------

/** «هر ۶ ساعت»، «هر روز»، «هر ۳ روز»: the title of an interval the server did not name. */
export const intervalTitle = (hours: number): string => {
  if (hours === 24) return 'هر روز'
  if (hours === 168) return 'هر هفته'
  if (hours === 336) return 'هر دو هفته'
  if (hours % 24 === 0) return `هر ${faDigits(hours / 24)} روز`
  return `هر ${faDigits(hours)} ساعت`
}

/** The presets, whether the server sent {hours, title} or only the hours. */
export const normalizeIntervals = (
  intervals: Array<ReportInterval | number> | undefined
): ReportInterval[] =>
  (intervals || [])
    .map((item) => (typeof item === 'number' ? {hours: item, title: intervalTitle(item)} : item))
    .filter((item) => Number.isFinite(item.hours) && item.hours > 0)

export const TRIGGER_TITLES: Record<string, string> = {
  scheduled: 'زمان‌بندی‌شده',
  manual: 'اجرای دستی',
}

export const triggerTitle = (trigger: string): string => TRIGGER_TITLES[trigger] || trigger

/**
 * The hours a schedule of `intervalHours` runs at in a day, starting at `sendTime`: every 6 hours from 08:00
 * runs at 08، 14، 20 and 02. Empty for intervals of a day or more, or ones that do not divide the day.
 */
export const runHoursOfDay = (intervalHours: number, sendTime: string): string[] => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(sendTime)
  // An interval that does not divide the day moves its hours from day to day: no fixed list.
  if (!match || intervalHours <= 0 || intervalHours >= 24 || 24 % intervalHours !== 0) return []
  const start = Number(match[1])
  const minutes = match[2]
  const hours: string[] = []
  for (let offset = 0; offset < 24; offset += intervalHours) {
    const hour = (start + offset) % 24
    hours.push(`${String(hour).padStart(2, '0')}${minutes === '00' ? '' : `:${minutes}`}`)
  }
  return hours
}
